import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  MessageSquare,
  Paperclip,
  Plus,
  Search,
  Send,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { relativeTime } from '@gbv/utils';
import { MAX_UPLOAD_BYTES } from '@gbv/types';
import { Avatar, Spinner, cn, useToast } from '@gbv/ui';
import { api, describeApiError } from '../lib/api';

/**
 * Personal chat: a direct line to a colleague, and groups people make.
 *
 * Three screens in one panel — the conversation list, a thread, and the picker
 * for starting something new. A phone-sized panel with three screens beats a
 * wider one that tries to show two at once, which is why every messaging app
 * on a small screen works this way.
 *
 * Nothing here is scoped to a client. That is the whole point of it being
 * separate from the Team tab beside it.
 */

interface Conversation {
  id: string;
  kind: 'DIRECT' | 'GROUP';
  title: string;
  otherUserId: string | null;
  memberCount: number;
  isAdmin: boolean;
  lastMessage: string | null;
  lastMessageAt: string | null;
  lastSenderName: string | null;
  unread: number;
}

interface Message {
  id: string;
  body: string;
  senderId: string;
  senderName: string;
  isMine: boolean;
  fileUrl: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
}

interface Person {
  id: string;
  name: string;
  role?: string;
}

type View = { screen: 'list' } | { screen: 'thread'; id: string } | { screen: 'new'; group: boolean };

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

export function PersonalChat() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>({ screen: 'list' });

  const conversations = useQuery({
    queryKey: ['personal-chat'],
    queryFn: () => api.get<{ data: Conversation[] }>('/internal/chat/conversations'),
    refetchInterval: 30_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['personal-chat'] });
    void queryClient.invalidateQueries({ queryKey: ['personal-unread'] });
  };

  if (view.screen === 'thread') {
    return (
      <Thread
        id={view.id}
        conversation={conversations.data?.data.find((c) => c.id === view.id)}
        onBack={() => {
          setView({ screen: 'list' });
          refresh();
        }}
      />
    );
  }

  if (view.screen === 'new') {
    return (
      <NewConversation
        group={view.group}
        onBack={() => setView({ screen: 'list' })}
        onCreated={(id) => {
          refresh();
          setView({ screen: 'thread', id });
        }}
      />
    );
  }

  const rows = conversations.data?.data ?? [];

  return (
    <>
      <div className="flex gap-1.5 border-b border-line px-3 py-2">
        <button
          type="button"
          onClick={() => setView({ screen: 'new', group: false })}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-control border border-line px-2 py-1.5 text-[12px] font-semibold text-ink-soft transition-colors hover:border-vir hover:text-vir"
        >
          <Plus size={13} />
          New chat
        </button>
        <button
          type="button"
          onClick={() => setView({ screen: 'new', group: true })}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-control border border-line px-2 py-1.5 text-[12px] font-semibold text-ink-soft transition-colors hover:border-vir hover:text-vir"
        >
          <Users size={13} />
          New group
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {conversations.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <MessageSquare size={20} className="text-ink-soft" />
            <p className="mt-2 text-sm text-ink">No personal chats yet</p>
            <p className="mt-1 text-xs text-ink-muted">
              Message a colleague directly, or make a group. Nobody else sees these.
            </p>
          </div>
        ) : (
          <ul>
            {rows.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => setView({ screen: 'thread', id: c.id })}
                  className="flex w-full items-center gap-2.5 border-b border-line-soft px-3 py-2.5 text-left transition-colors hover:bg-surface-sunken"
                >
                  {c.kind === 'GROUP' ? (
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-vir/10 text-vir">
                      <Users size={15} />
                    </span>
                  ) : (
                    <Avatar name={c.title} size="sm" />
                  )}

                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[13px] font-semibold text-ink">{c.title}</span>
                      {c.lastMessageAt && (
                        <span className="shrink-0 text-[10px] text-ink-soft">
                          {relativeTime(new Date(c.lastMessageAt))}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-[11px] text-ink-muted">
                        {c.lastMessage
                          ? `${c.kind === 'GROUP' && c.lastSenderName ? `${c.lastSenderName}: ` : ''}${c.lastMessage}`
                          : c.kind === 'GROUP'
                            ? `${c.memberCount} people`
                            : 'No messages yet'}
                      </span>
                      {c.unread > 0 && (
                        <span className="flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
                          {c.unread > 99 ? '99+' : c.unread}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function Thread({
  id,
  conversation,
  onBack,
}: {
  id: string;
  conversation: Conversation | undefined;
  onBack: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [pendingFile, setPendingFile] = useState<{ url: string; fileName: string; fileSizeBytes: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['personal-thread', id],
    queryFn: () =>
      api.get<{ messages: Message[]; members: Person[] }>(`/internal/chat/${id}/messages`),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread.data?.messages.length]);

  const send = useMutation({
    mutationFn: async (body: string) => {
      let fileUrl: string | null = null;
      let fileName: string | null = null;
      let fileSizeBytes: number | null = null;

      if (pendingFile) {
        fileUrl = pendingFile.url;
        fileName = pendingFile.fileName;
        fileSizeBytes = pendingFile.fileSizeBytes;
      }

      return api.post(`/internal/chat/${id}/messages`, { body, fileUrl, fileName, fileSizeBytes });
    },
    onSuccess: () => {
      setDraft('');
      setPendingFile(null);
      void queryClient.invalidateQueries({ queryKey: ['personal-thread', id] });
      void queryClient.invalidateQueries({ queryKey: ['personal-chat'] });
    },
    onError: (err) => toast.error('Not sent', describeApiError(err)),
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('That file is too large', `The limit is ${MAX_UPLOAD_BYTES / 1024 / 1024}MB.`);
      return;
    }
    try {
      const form = new FormData();
      form.append('file', file);
      const result = await api.postForm<{ url: string; fileName: string; fileSizeBytes: number }>(
        '/internal/attachments',
        form,
      );
      setPendingFile(result);
    } catch (err) {
      toast.error('Upload failed', describeApiError(err));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const messages = thread.data?.messages ?? [];
  const isGroup = conversation?.kind === 'GROUP';

  return (
    <>
      <div className="flex items-center gap-2 border-b border-line px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to chats"
          className="rounded p-1 text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink"
        >
          <ArrowLeft size={16} />
        </button>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-ink">
            {conversation?.title ?? 'Chat'}
          </span>
          <span className="block truncate text-[10px] text-ink-soft">
            {isGroup
              ? `${thread.data?.members.length ?? conversation?.memberCount ?? 0} people · only they can read this`
              : 'Private — only the two of you'}
          </span>
        </span>
        {isGroup && conversation?.isAdmin && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            aria-label="Add people to this group"
            className="rounded p-1 text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <UserPlus size={15} />
          </button>
        )}
      </div>

      {adding && (
        <AddMembers
          id={id}
          existing={thread.data?.members ?? []}
          onDone={() => {
            setAdding(false);
            void queryClient.invalidateQueries({ queryKey: ['personal-thread', id] });
            void queryClient.invalidateQueries({ queryKey: ['personal-chat'] });
          }}
        />
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {thread.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : messages.length === 0 ? (
          <p className="mt-6 text-center text-xs text-ink-muted">
            Nothing yet. Say something.
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={cn('flex', m.isMine ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[80%] rounded-card px-2.5 py-1.5',
                  m.isMine ? 'bg-vir text-white' : 'bg-surface-sunken text-ink',
                )}
              >
                {isGroup && !m.isMine && (
                  <p className="mb-0.5 text-[10px] font-semibold text-vir-dark">{m.senderName}</p>
                )}
                {m.fileUrl && (
                  <a
                    href={m.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'mb-1.5 flex items-center gap-1.5 rounded-pill px-2 py-1 text-[11px]',
                      m.isMine ? 'bg-white/15 text-white' : 'bg-line text-ink-muted',
                    )}
                  >
                    <Paperclip size={10} />
                    <span className="min-w-0 flex-1 truncate">{m.fileName}</span>
                    {m.fileSizeBytes && <span className="shrink-0 opacity-80">{formatFileSize(m.fileSizeBytes)}</span>}
                  </a>
                )}
                <p className="whitespace-pre-wrap break-words text-[13px]">{m.body}</p>
                <p
                  className={cn(
                    'mt-0.5 text-[9px]',
                    m.isMine ? 'text-white/70' : 'text-ink-soft',
                  )}
                >
                  {relativeTime(new Date(m.createdAt))}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <form
        className="flex items-end gap-1.5 border-t border-line p-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim() || pendingFile) send.mutate(draft.trim());
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (draft.trim() || pendingFile) send.mutate(draft.trim());
            }
          }}
          rows={1}
          placeholder="Write a message"
          className="max-h-24 min-h-[34px] flex-1 resize-none rounded-control border border-line px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-vir"
        />
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-control border border-line text-ink-soft transition-colors hover:border-vir hover:text-vir"
          aria-label="Attach file"
        >
          <Paperclip size={15} />
        </button>
        <button
          type="submit"
          disabled={(!draft.trim() && !pendingFile) || send.isPending}
          aria-label="Send"
          className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-control bg-vir text-white transition-colors hover:bg-vir-dark disabled:opacity-40"
        >
          <Send size={15} />
        </button>
      </form>
    </>
  );
}

/** The staff directory, filtered, for starting a chat or filling a group. */
function usePeople(search: string) {
  return useQuery({
    queryKey: ['chat-people'],
    queryFn: () => api.get<{ data: Person[] }>('/internal/staff'),
    select: (d) => {
      const term = search.trim().toLowerCase();
      const rows = d.data ?? [];
      return term ? rows.filter((p) => p.name.toLowerCase().includes(term)) : rows;
    },
  });
}

function NewConversation({
  group,
  onBack,
  onCreated,
}: {
  group: boolean;
  onBack: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const people = usePeople(search);

  const create = useMutation({
    mutationFn: () =>
      group
        ? api.post<{ id: string }>('/internal/chat/groups', {
            title: title.trim(),
            memberIds: [...picked],
          })
        : api.post<{ id: string }>('/internal/chat/direct', { userId: [...picked][0] }),
    onSuccess: (c) => onCreated(c.id),
    onError: (err) => toast.error('Could not start it', describeApiError(err)),
  });

  const ready = group ? title.trim().length > 0 && picked.size > 0 : picked.size === 1;

  return (
    <>
      <div className="flex items-center gap-2 border-b border-line px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to chats"
          className="rounded p-1 text-ink-soft transition-colors hover:bg-surface-sunken hover:text-ink"
        >
          <ArrowLeft size={16} />
        </button>
        <span className="text-[13px] font-semibold text-ink">
          {group ? 'New group' : 'Message someone'}
        </span>
      </div>

      {group && (
        <div className="border-b border-line px-3 py-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Group name — Diwali campaign, Design huddle…"
            className="w-full rounded-control border border-line px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-vir"
          />
        </div>
      )}

      <div className="border-b border-line px-3 py-2">
        <div className="flex items-center gap-1.5 rounded-control border border-line px-2 py-1.5">
          <Search size={13} className="shrink-0 text-ink-soft" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search colleagues"
            className="min-w-0 flex-1 text-[12px] text-ink outline-none"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {people.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <ul>
            {(people.data ?? []).map((person) => {
              const on = picked.has(person.id);
              return (
                <li key={person.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setPicked((prev) => {
                        const next = new Set(group ? prev : []);
                        if (on) next.delete(person.id);
                        else next.add(person.id);
                        return next;
                      })
                    }
                    className="flex w-full items-center gap-2.5 border-b border-line-soft px-3 py-2 text-left transition-colors hover:bg-surface-sunken"
                  >
                    <Avatar name={person.name} size="xs" />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                      {person.name}
                    </span>
                    {on && <Check size={14} className="shrink-0 text-go" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-line p-2">
        <button
          type="button"
          disabled={!ready || create.isPending}
          onClick={() => create.mutate()}
          className="w-full rounded-control bg-vir px-3 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-vir-dark disabled:opacity-40"
        >
          {group
            ? `Create group${picked.size > 0 ? ` with ${picked.size}` : ''}`
            : 'Start chatting'}
        </button>
      </div>
    </>
  );
}

/** Adding people to a group already open, without leaving the thread. */
function AddMembers({
  id,
  existing,
  onDone,
}: {
  id: string;
  existing: Person[];
  onDone: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const people = usePeople(search);
  const already = new Set(existing.map((p) => p.id));

  const add = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/internal/chat/${id}/members`, { memberIds: [userId] }),
    onSuccess: () => {
      toast.success('Added', 'They can see the whole thread from here on.');
      onDone();
    },
    onError: (err) => toast.error('Could not add them', describeApiError(err)),
  });

  const candidates = (people.data ?? []).filter((p) => !already.has(p.id));

  return (
    <div className="max-h-44 shrink-0 overflow-y-auto border-b border-line bg-surface-page">
      <div className="sticky top-0 flex items-center gap-1.5 border-b border-line-soft bg-surface-page px-3 py-1.5">
        <Search size={12} className="shrink-0 text-ink-soft" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Add someone"
          className="min-w-0 flex-1 bg-transparent text-[12px] text-ink outline-none"
        />
        <button
          type="button"
          onClick={onDone}
          className="shrink-0 text-[11px] font-semibold text-ink-soft hover:text-ink"
        >
          Done
        </button>
      </div>

      {candidates.length === 0 ? (
        <p className="px-3 py-2 text-[11px] text-ink-muted">Everyone is already here.</p>
      ) : (
        candidates.map((p) => (
          <button
            key={p.id}
            type="button"
            disabled={add.isPending}
            onClick={() => add.mutate(p.id)}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-surface-sunken disabled:opacity-50"
          >
            <Avatar name={p.name} size="xs" />
            <span className="min-w-0 flex-1 truncate text-[12px] text-ink">{p.name}</span>
            <Plus size={12} className="shrink-0 text-ink-soft" />
          </button>
        ))
      )}
    </div>
  );
}
